import { useCallback } from 'react';

import { useSessionStorageValueAsync } from 'src/app/shared/hooks/storage';

type Result = {
	isBannerDismissed: (bannerKey: string) => boolean;
	setBannerDismissed: (bannerKey: string) => void;
	clearBannerDismissed: (bannerKey: string) => void;
	clearAllDismissed: () => void;
};

/**
 * Hook to manage banner dismissal state using sessionStorage.
 * This allows banners to stay dismissed for the current session only.
 */
export function useBannerDismissalStorage(): Result {
	const STORAGE_KEY = 'starterDashboard_dismissedBanners';
	const [dismissedBannersString, setDismissedBannersString] = useSessionStorageValueAsync(STORAGE_KEY);

	// Parse the dismissed banners from session storage
	const getDismissedBanners = useCallback((): Set<string> => {
		// Fallback to direct sessionStorage check if async hook hasn't loaded yet
		const jsonString =
			dismissedBannersString === undefined ? sessionStorage.getItem(STORAGE_KEY) : dismissedBannersString;

		try {
			const parsed: unknown = jsonString ? JSON.parse(jsonString) : [];
			const bannerArray = Array.isArray(parsed)
				? parsed.filter((item): item is string => typeof item === 'string')
				: [];
			return new Set(bannerArray);
		} catch {
			return new Set();
		}
	}, [dismissedBannersString]);

	// Check if a specific banner is dismissed
	const isBannerDismissed = useCallback(
		(bannerKey: string): boolean => {
			return getDismissedBanners().has(bannerKey);
		},
		[getDismissedBanners],
	);

	// Mark a banner as dismissed
	const setBannerDismissed = useCallback(
		(bannerKey: string) => {
			const dismissedBanners = getDismissedBanners();
			dismissedBanners.add(bannerKey);
			setDismissedBannersString(JSON.stringify([...dismissedBanners]));
		},
		[getDismissedBanners, setDismissedBannersString],
	);

	// Clear dismissal for a specific banner
	const clearBannerDismissed = useCallback(
		(bannerKey: string) => {
			const dismissedBanners = getDismissedBanners();
			dismissedBanners.delete(bannerKey);
			setDismissedBannersString(JSON.stringify([...dismissedBanners]));
		},
		[getDismissedBanners, setDismissedBannersString],
	);

	// Clear all dismissed banners
	const clearAllDismissed = useCallback(() => {
		setDismissedBannersString(null);
	}, [setDismissedBannersString]);

	return {
		isBannerDismissed,
		setBannerDismissed,
		clearBannerDismissed,
		clearAllDismissed,
	};
}
